import React, { useState, useEffect } from 'react';
import { Users, BookOpen, BarChart3, Shield, Check, X, AlertTriangle, Upload, FileJson, Clock, CheckCircle, XCircle, BookMarked } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingLevel4, setPendingLevel4] = useState([]);
  const [disputed, setDisputed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, pendingRes, disputedRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users?limit=50'),
        api.get('/admin/users/level4-pending'),
        api.get('/admin/disputed')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setPendingLevel4(pendingRes.data.users || []);
      setDisputed(disputedRes.data.disputed || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const handleApproveLevel4 = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approve-level4`);
      await loadData();
    } catch (err) { console.error(err) }
  };

  const handleRejectLevel4 = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/reject-level4`);
      await loadData();
    } catch (err) { console.error(err) }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Shield className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-[#1C1917]">Admin Access Required</h2>
        <p className="text-sm text-[#57534E] mt-2">You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="admin-title">
        Admin Dashboard
      </h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F5F4] rounded-xl mb-6">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="level4" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-level4">
            Wirfon {pendingLevel4.length > 0 && <span className="ml-1 w-5 h-5 rounded-full bg-[#4CAF50] text-white text-xs flex items-center justify-center">{pendingLevel4.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="disputed" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-disputed">Disputed</TabsTrigger>
          <TabsTrigger value="ingest" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-ingest">
            <Upload className="w-3.5 h-3.5 mr-1" /> Ingest Data
          </TabsTrigger>
          <TabsTrigger value="banners" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-banners">
            Banners
          </TabsTrigger>
          <TabsTrigger value="assistants" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-assistants">
            Assistants
          </TabsTrigger>
          <TabsTrigger value="bots" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-bots">
            Bots
          </TabsTrigger>
          <TabsTrigger value="reviewed" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-reviewed">
            Reviewed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
            <AdminStat label="Users" value={stats?.total_users || 0} icon={<Users className="w-4 h-4 text-[#4CAF50]" />} testId="admin-stat-users" />
            <AdminStat label="Words" value={stats?.total_words || 0} icon={<BookOpen className="w-4 h-4 text-[#588157]" />} testId="admin-stat-words" />
            <AdminStat label="Translations" value={stats?.total_translations || 0} icon={<BarChart3 className="w-4 h-4 text-[#D4A373]" />} testId="admin-stat-translations" />
            <AdminStat label="Confirmed" value={stats?.confirmed_translations || 0} icon={<Check className="w-4 h-4 text-[#588157]" />} testId="admin-stat-confirmed" />
            <AdminStat label="Disputed" value={stats?.disputed_translations || 0} icon={<AlertTriangle className="w-4 h-4 text-[#4CAF50]" />} testId="admin-stat-disputed" />
            <AdminStat label="Validations" value={stats?.total_validations || 0} testId="admin-stat-validations" />
            <AdminStat label="Posts" value={stats?.total_posts || 0} testId="admin-stat-posts" />
            <AdminStat label="Questions" value={stats?.total_queries || 0} testId="admin-stat-queries" />
            <AdminStat label="Interactions" value={stats?.total_interactions || 0} testId="admin-stat-interactions" />
            <AdminStat label="L4 Pending" value={stats?.level4_pending || 0} icon={<Shield className="w-4 h-4 text-[#D4A373]" />} testId="admin-stat-level4" />
          </div>
          <h3 className="text-xs font-bold text-[#57534E] uppercase tracking-wider mb-3">Enrichment Corpora</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <AdminStat label="Literature Verses" value={stats?.bible_verses || 0} icon={<BookMarked className="w-4 h-4 text-[#78716C]" />} testId="admin-stat-bible" />
            <AdminStat label="Lit. Aligned" value={stats?.bible_aligned || 0} icon={<Check className="w-4 h-4 text-[#78716C]" />} testId="admin-stat-bible-aligned" />
            <AdminStat label="Source Books" value={stats?.bible_books || 0} testId="admin-stat-bible-books" />
            <AdminStat label="Dict Senses" value={stats?.dict_senses || 0} testId="admin-stat-senses" />
            <AdminStat label="Dict Examples" value={stats?.dict_examples || 0} testId="admin-stat-dict-examples" />
          </div>
          <h3 className="text-xs font-bold text-[#57534E] uppercase tracking-wider mb-3 mt-5">Community Suggestions</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <AdminStat label="Pending" value={stats?.suggestions_pending || 0} icon={<Users className="w-4 h-4 text-[#0891B2]" />} testId="admin-stat-sug-pending" />
            <AdminStat label="Approved" value={stats?.suggestions_approved || 0} icon={<Check className="w-4 h-4 text-[#588157]" />} testId="admin-stat-sug-approved" />
            <AdminStat label="Rejected" value={stats?.suggestions_rejected || 0} icon={<X className="w-4 h-4 text-[#4CAF50]" />} testId="admin-stat-sug-rejected" />
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="mb-3">
            <input type="text" placeholder="Search users by name or email..."
              onChange={(e) => {
                const q = e.target.value.toLowerCase();
                document.querySelectorAll('[data-testid^="admin-user-"]').forEach(row => {
                  const text = row.textContent.toLowerCase();
                  row.style.display = text.includes(q) ? '' : 'none';
                });
              }}
              className="w-full rounded-xl border-0 py-2.5 px-4 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
              data-testid="admin-user-search" />
          </div>
          <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid="admin-users-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E7E5E4] bg-[#F5F5F4]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">XP</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#57534E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]" data-testid={`admin-user-${u.user_id}`}>
                      <td className="px-4 py-3 font-medium text-[#1C1917]">{u.name}</td>
                      <td className="px-4 py-3 text-[#57534E]">{u.email}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E8F5E9] text-[#4CAF50]">L{u.level}</span></td>
                      <td className="px-4 py-3 text-[#57534E]">{u.xp_points}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold ${u.role === 'admin' ? 'text-[#4CAF50]' : 'text-[#57534E]'}`}>{u.role}</span></td>
                      <td className="px-4 py-3">
                        <button onClick={async () => {
                          const pw = window.prompt(`Set temporary password for ${u.name}:`, `LamFon${Math.floor(Math.random()*9000)+1000}`);
                          if (!pw) return;
                          try {
                            await api.post('/admin/reset-user-password', { user_id: u.user_id, temp_password: pw });
                            alert(`Password reset for ${u.name}. Temp password: ${pw}\nThey will be prompted to change it on login.`);
                          } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
                        }} className="text-[9px] font-semibold px-2 py-0.5 rounded-lg bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]"
                        data-testid={`reset-pw-${u.user_id}`}>Reset PW</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="level4">
          {pendingLevel4.length > 0 ? (
            <div className="space-y-3" data-testid="level4-pending-list">
              {pendingLevel4.map(u => (
                <div key={u.user_id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5 flex items-center justify-between" data-testid={`level4-pending-${u.user_id}`}>
                  <div>
                    <p className="font-semibold text-[#1C1917]">{u.name}</p>
                    <p className="text-xs text-[#57534E]">{u.email} &middot; Level {u.level} &middot; {u.xp_points} XP</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleApproveLevel4(u.user_id)} size="sm"
                      className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl" data-testid={`approve-${u.user_id}`}>
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button onClick={() => handleRejectLevel4(u.user_id)} size="sm" variant="outline"
                      className="border-[#4CAF50] text-[#4CAF50] rounded-xl hover:bg-[#E8F5E9]" data-testid={`reject-${u.user_id}`}>
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-[#A8A29E] py-8">No pending Wirfon requests</p>
          )}
        </TabsContent>

        <TabsContent value="disputed">
          {disputed.length > 0 ? (
            <div className="space-y-3" data-testid="disputed-translations-list">
              {disputed.map(t => (
                <div key={t.translation_id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`disputed-${t.translation_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-[#1C1917]">{t.lamnso_word?.text || '?'}</span>
                      <span className="mx-2 text-[#A8A29E]">-&gt;</span>
                      <span className="font-semibold text-[#4CAF50]">{t.english_word?.text || '?'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#57534E]">Confidence: {Math.round(t.confidence_score * 100)}%</span>
                      <span className="text-xs bg-[#E8F5E9] text-[#4CAF50] px-2 py-0.5 rounded-full font-semibold">Disputed</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-[#A8A29E] py-8">No disputed translations</p>
          )}
        </TabsContent>

        <TabsContent value="ingest">
          <DataIngestionPanel />
        </TabsContent>

        <TabsContent value="banners">
          <BannerManager />
        </TabsContent>

        <TabsContent value="assistants">
          <AssistantReactionsManager />
        </TabsContent>

        <TabsContent value="bots">
          <BotManager />
        </TabsContent>

        <TabsContent value="reviewed">
          <ReviewedWordsList />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdminStat({ label, value, icon, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4" data-testid={testId}>
      {icon && <div className="mb-2">{icon}</div>}
      <p className="text-2xl font-bold text-[#1C1917]">{value}</p>
      <p className="text-xs text-[#A8A29E]">{label}</p>
    </div>
  );
}

const INGEST_ENDPOINTS = [
  { key: 'translations', label: 'Translations (Live Backbone)', endpoint: '/ingest/translations', desc: 'Canonical translation pairs ({lamnso, english, confidence, status, source})', accept: '*.json' },
  { key: 'enrich_senses', label: 'Dictionary Senses (Enrichment)', endpoint: '/enrich/senses', desc: 'Dictionary senses linked to headwords. Does NOT overwrite translations. ({lamnso, sense, sense_order, pos, source})', accept: '*.json' },
  { key: 'enrich_examples', label: 'Dictionary Examples (Enrichment)', endpoint: '/enrich/examples', desc: 'Example sentences linked to headwords. ({headword, lamnso, english, source})', accept: '*.json' },
  { key: 'dictionary', label: 'Dictionary Entries (Legacy)', endpoint: '/ingest/dictionary', desc: 'Full entries with words, definitions, examples', accept: '*.json' },
  { key: 'words', label: 'Words', endpoint: '/ingest/words', desc: 'Simple word list ({lamnso, normalized, source})', accept: '*.json' },
];

function DataIngestionPanel() {
  const [results, setResults] = useState({});
  const [uploading, setUploading] = useState({});
  const [clearFlags, setClearFlags] = useState({});
  const [confirmClear, setConfirmClear] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/ingest/logs?limit=30');
      setLogs(res.data.logs || []);
    } catch (err) { console.error(err) }
    setLogsLoading(false);
  };

  const handleFileChange = (key, e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFiles(prev => ({ ...prev, [key]: file }));
  };

  const handleUpload = async (endpointConfig) => {
    const file = selectedFiles[endpointConfig.key];
    if (!file) {
      setResults(prev => ({
        ...prev,
        [endpointConfig.key]: { success: false, error: 'Please select a JSON file first' }
      }));
      return;
    }

    const shouldClear = clearFlags[endpointConfig.key] || false;

    // If clear is checked but not yet confirmed, show inline confirmation
    if (shouldClear && !confirmClear[endpointConfig.key]) {
      setConfirmClear(prev => ({ ...prev, [endpointConfig.key]: true }));
      return;
    }

    // Reset confirmation state
    setConfirmClear(prev => ({ ...prev, [endpointConfig.key]: false }));

    const formData = new FormData();
    formData.append('file', file);

    const url = shouldClear ? `${endpointConfig.endpoint}?clear_existing=true` : endpointConfig.endpoint;

    setUploading(prev => ({ ...prev, [endpointConfig.key]: true }));
    setResults(prev => ({ ...prev, [endpointConfig.key]: null }));

    try {
      const res = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
      setResults(prev => ({ ...prev, [endpointConfig.key]: { success: true, data: res.data } }));
      loadLogs();
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || 'Upload failed';
      setResults(prev => ({
        ...prev,
        [endpointConfig.key]: { success: false, error: errMsg }
      }));
    }
    setUploading(prev => ({ ...prev, [endpointConfig.key]: false }));
    setSelectedFiles(prev => ({ ...prev, [endpointConfig.key]: null }));
  };

  const cancelClear = (key) => {
    setConfirmClear(prev => ({ ...prev, [key]: false }));
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString();
  };

  return (
    <div className="space-y-6" data-testid="data-ingestion-panel">
      {/* Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {INGEST_ENDPOINTS.map((ep) => (
          <div key={ep.key} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`ingest-card-${ep.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <FileJson className="w-5 h-5 text-[#4CAF50]" />
              <h3 className="text-sm font-semibold text-[#1C1917]">{ep.label}</h3>
            </div>
            <p className="text-xs text-[#57534E] mb-3">{ep.desc}</p>
            <p className="text-xs text-[#A8A29E] mb-3">Expected file: <code className="bg-[#F5F5F4] px-1.5 py-0.5 rounded">{ep.accept}</code></p>

            <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid={`clear-flag-${ep.key}`}>
              <input
                type="checkbox"
                checked={clearFlags[ep.key] || false}
                onChange={(e) => setClearFlags(prev => ({ ...prev, [ep.key]: e.target.checked }))}
                className="w-4 h-4 rounded border-[#E7E5E4] text-[#4CAF50] focus:ring-[#4CAF50]"
              />
              <span className={`text-xs font-medium ${clearFlags[ep.key] ? 'text-[#4CAF50]' : 'text-[#57534E]'}`}>
                Clear existing data before import
              </span>
            </label>

            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".json"
                onChange={(e) => handleFileChange(ep.key, e)}
                className="text-xs file:mr-2 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-[#E8F5E9] file:text-[#4CAF50] hover:file:bg-[#C8E6C9] file:cursor-pointer flex-1"
                data-testid={`ingest-file-${ep.key}`}
              />
              <Button
                size="sm"
                onClick={() => handleUpload(ep)}
                disabled={uploading[ep.key]}
                className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs px-4"
                data-testid={`ingest-upload-${ep.key}`}
              >
                {uploading[ep.key] ? (
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center gap-1"><Upload className="w-3 h-3" /> Upload</span>
                )}
              </Button>
            </div>
            {selectedFiles[ep.key] && (
              <p className="text-[10px] text-[#588157] mt-1">Selected: {selectedFiles[ep.key].name} ({(selectedFiles[ep.key].size / 1024).toFixed(1)} KB)</p>
            )}

            {/* Inline clear confirmation */}
            {confirmClear[ep.key] && (
              <div className="mt-3 p-3 rounded-xl bg-[#E8F5E9] border border-[#4CAF50]/30" data-testid={`confirm-clear-${ep.key}`}>
                <p className="text-xs font-semibold text-[#4CAF50] mb-2">
                  This will DELETE all existing {ep.label.toLowerCase()} data before importing. Are you sure?
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleUpload(ep)}
                    className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs"
                    data-testid={`confirm-clear-yes-${ep.key}`}
                  >
                    Yes, clear and import
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => cancelClear(ep.key)}
                    className="rounded-xl text-xs" data-testid={`confirm-clear-no-${ep.key}`}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Result */}
            {results[ep.key] && (
              <div className={`mt-3 p-3 rounded-xl text-xs ${
                results[ep.key].success ? 'bg-[#EAF1EA] text-[#588157]' : 'bg-[#E8F5E9] text-[#4CAF50]'
              }`} data-testid={`ingest-result-${ep.key}`}>
                {results[ep.key].success ? (
                  <div>
                    <div className="flex items-center gap-1 font-semibold mb-1">
                      <CheckCircle className="w-3.5 h-3.5" /> Ingestion Complete
                    </div>
                    <div className="space-y-0.5">
                      {results[ep.key].data.cleared && (
                        <p className="text-[#4CAF50]">Cleared: {Object.entries(results[ep.key].data.cleared).map(([k,v]) => `${k}: ${v}`).join(', ')}</p>
                      )}
                      <p>Total records: <strong>{results[ep.key].data.total_records}</strong></p>
                      <p>Inserted: <strong>{results[ep.key].data.inserted}</strong></p>
                      <p>Skipped (duplicates): <strong>{results[ep.key].data.skipped}</strong></p>
                      <p>Errors: <strong>{results[ep.key].data.errors}</strong></p>
                    </div>
                    {results[ep.key].data.details?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer font-semibold">Details ({results[ep.key].data.details.length})</summary>
                        <ul className="mt-1 space-y-0.5 text-[10px] max-h-32 overflow-y-auto">
                          {results[ep.key].data.details.map((d, i) => (
                            <li key={`idx-${i}`} className="opacity-80">{d}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> {results[ep.key].error}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Ingestion Logs */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid="ingestion-logs">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#D4A373]" />
            <h3 className="text-sm font-semibold text-[#1C1917]">Ingestion History</h3>
          </div>
          <Button size="sm" variant="outline" onClick={loadLogs} disabled={logsLoading}
            className="rounded-xl text-xs" data-testid="refresh-logs-button">
            {logsLoading ? '...' : 'Refresh'}
          </Button>
        </div>

        {logs.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.map((log) => (
              <div key={log.log_id} className="border border-[#E7E5E4] rounded-xl p-3" data-testid={`log-${log.log_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-[#1C1917]">
                    {log.source_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="text-[10px] text-[#A8A29E]">{formatTime(log.created_at)}</span>
                </div>
                {log.summary && (
                  <div className="flex gap-4 text-[10px] text-[#57534E]">
                    {log.summary.source_file && <span>File: {log.summary.source_file}</span>}
                    <span>Total: {log.summary.total_records}</span>
                    <span className="text-[#588157]">Inserted: {log.summary.inserted}</span>
                    <span className="text-[#D4A373]">Skipped: {log.summary.skipped}</span>
                    {log.summary.errors > 0 && <span className="text-[#4CAF50]">Errors: {log.summary.errors}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#A8A29E] text-center py-4">No ingestion logs yet</p>
        )}
      </div>

      {/* Future Data Sources Note */}
      <div className="bg-[#F5F5F4] rounded-2xl border border-[#E7E5E4] p-5" data-testid="future-sources-note">
        <h3 className="text-xs font-semibold text-[#57534E] mb-2">Coming Soon</h3>
        <div className="flex flex-wrap gap-2">
          {['index.json (English candidates)', 'bible_sentences.json (aligned corpus)', 'audio.json (pronunciation data)'].map((s) => (
            <span key={s} className="text-[10px] px-2.5 py-1 rounded-lg bg-white border border-[#E7E5E4] text-[#A8A29E]">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}


function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '', tagline: '', url: '', image_url: '', cta_text: 'Learn More',
    label: 'From LamFon', frequency: 9, start_after: 4, is_active: true
  });

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const res = await api.get('/banners?active_only=false');
      setBanners(res.data.banners || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: '', tagline: '', url: '', image_url: '', cta_text: 'Learn More', label: 'From LamFon', frequency: 9, start_after: 4, is_active: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/banners/${editing}`, form);
      } else {
        await api.post('/banners', form);
      }
      resetForm();
      loadBanners();
    } catch (err) { console.error(err) }
  };

  const handleEdit = (banner) => {
    setForm({
      title: banner.title, tagline: banner.tagline, url: banner.url,
      image_url: banner.image_url, cta_text: banner.cta_text, label: banner.label,
      frequency: banner.frequency, start_after: banner.start_after, is_active: banner.is_active
    });
    setEditing(banner.banner_id);
    setShowForm(true);
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await api.delete(`/banners/${bannerId}`);
      loadBanners();
    } catch (err) { console.error(err) }
  };

  const handleToggle = async (banner) => {
    try {
      await api.put(`/banners/${banner.banner_id}`, { ...banner, is_active: !banner.is_active });
      loadBanners();
    } catch (err) { console.error(err) }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4" data-testid="banner-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917]">Promotional Banners</h3>
          <p className="text-[10px] text-[#A8A29E]">Manage in-feed banners for the Community feed</p>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs" data-testid="add-banner-btn">
          + New Banner
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 space-y-3" data-testid="banner-form">
          <h4 className="text-xs font-semibold text-[#1C1917]">{editing ? 'Edit Banner' : 'New Banner'}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Title *</label>
              <input value={form.title} onChange={(e) => updateField('title', e.target.value)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="LamFon Books" data-testid="banner-title-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Tagline</label>
              <input value={form.tagline} onChange={(e) => updateField('tagline', e.target.value)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="Children's books for learning Lamnso'" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Destination URL *</label>
              <input value={form.url} onChange={(e) => updateField('url', e.target.value)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="https://lamfonbooks.com" data-testid="banner-url-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-medium text-[#57534E]">Banner Image</label>
              <div className="flex items-center gap-2 mt-1">
                <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-colors ${uploading ? 'bg-[#E7E5E4] text-[#A8A29E]' : 'bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9]'}`}>
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await api.post('/uploads/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        updateField('image_url', res.data.full_url || res.data.url);
                      } catch (err) {
                        alert('Upload failed: ' + (err.response?.data?.detail || err.message));
                      }
                      setUploading(false);
                      e.target.value = '';
                    }}
                    data-testid="banner-image-upload" />
                </label>
                <span className="text-[10px] text-[#A8A29E]">or</span>
                <input value={form.image_url} onChange={(e) => updateField('image_url', e.target.value)}
                  className="flex-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                  placeholder="Paste image URL..." />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">CTA Text</label>
              <input value={form.cta_text} onChange={(e) => updateField('cta_text', e.target.value)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="Explore Books" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Label</label>
              <input value={form.label} onChange={(e) => updateField('label', e.target.value)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="From LamFon" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Show every N posts</label>
              <input type="number" value={form.frequency} onChange={(e) => updateField('frequency', parseInt(e.target.value) || 9)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                min="3" max="50" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Start after position</label>
              <input type="number" value={form.start_after} onChange={(e) => updateField('start_after', parseInt(e.target.value) || 4)}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                min="1" max="20" />
            </div>
          </div>
          {form.image_url && (
            <div className="rounded-xl overflow-hidden border border-[#E7E5E4] max-w-xs">
              <img src={form.image_url} alt="Preview" className="w-full aspect-[3/2] object-cover" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-[#57534E]">
              <input type="checkbox" checked={form.is_active} onChange={(e) => updateField('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-[#E7E5E4] text-[#4CAF50] focus:ring-[#4CAF50]" />
              Active
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={!form.title || !form.url}
              className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs" data-testid="save-banner-btn">
              {editing ? 'Update Banner' : 'Create Banner'}
            </Button>
            <Button size="sm" variant="outline" onClick={resetForm} className="rounded-xl text-xs">Cancel</Button>
          </div>
        </div>
      )}

      {/* Banner List */}
      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : banners.length > 0 ? (
        <div className="space-y-3" data-testid="banner-list">
          {banners.map(b => (
            <div key={b.banner_id} className={`bg-white rounded-2xl border border-[#E7E5E4] p-4 ${!b.is_active ? 'opacity-50' : ''}`} data-testid={`admin-banner-${b.banner_id}`}>
              <div className="flex items-start gap-3">
                {b.image_url && (
                  <img src={b.image_url} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-[#1C1917] truncate">{b.title}</h4>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.is_active ? 'bg-[#EAF1EA] text-[#588157]' : 'bg-[#F5F5F4] text-[#A8A29E]'}`}>
                      {b.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#57534E] truncate">{b.tagline}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-[#A8A29E]">
                    <span>Every {b.frequency} posts</span>
                    <span>After pos {b.start_after}</span>
                    <span>{b.clicks || 0} clicks</span>
                    <span>{b.impressions || 0} views</span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => handleToggle(b)}
                    className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]">
                    {b.is_active ? 'Pause' : 'Activate'}
                  </button>
                  <button onClick={() => handleEdit(b)}
                    className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(b.banner_id)}
                    className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9]">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-xs text-[#A8A29E]">
          No banners yet. Click "+ New Banner" to create one.
        </div>
      )}
    </div>
  );
}


const TAABIY_IMG = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/ed75ipfo_Ta%C3%A0biy.png';
const YEEBIY_IMG = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/49uszg6d_Ye%C3%A8biy.png';

const TRIGGER_LABELS = {
  greeting: 'Greeting', lesson_complete: 'Lesson Complete', quiz_pass: 'Quiz Pass',
  quiz_fail: 'Quiz Fail', streak_milestone: 'Streak Milestone', level_up: 'Level Up',
  first_search: 'First Search', first_post: 'First Post', suggestion_approved: 'Suggestion Approved',
  validation_complete: 'Validation Done', achievement_earned: 'Achievement', comeback: 'Welcome Back',
  encouragement: 'Encouragement', celebration: 'Celebration', thinking: 'Thinking',
  wave: 'Wave', idle: 'Idle', custom: 'Custom'
};

function AssistantReactionsManager() {
  const [reactions, setReactions] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterAssistant, setFilterAssistant] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [uploadForm, setUploadForm] = useState({ assistant: 'taabiy', trigger_event: 'greeting', label: '', target_gender: 'all', target_age_group: 'all' });
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    loadReactions();
    api.get('/reactions/triggers').then(r => setTriggers(r.data.triggers || [])).catch(() => {});
  }, [filterAssistant, filterTrigger]); // eslint-disable-line

  const loadReactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAssistant) params.set('assistant', filterAssistant);
      if (filterTrigger) params.set('trigger', filterTrigger);
      params.set('active_only', 'false');
      const res = await api.get(`/reactions?${params.toString()}`);
      setReactions(res.data.reactions || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({
        assistant: uploadForm.assistant,
        trigger_event: uploadForm.trigger_event,
        label: uploadForm.label || `${uploadForm.assistant} ${uploadForm.trigger_event}`,
        target_gender: uploadForm.target_gender,
        target_age_group: uploadForm.target_age_group
      });
      await api.post(`/reactions?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      loadReactions();
      setShowUpload(false);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleToggle = async (r) => {
    await api.put(`/reactions/${r.reaction_id}`, { is_active: !r.is_active }).catch(() => {});
    loadReactions();
  };

  const handleDelete = async (reactionId) => {
    if (!window.confirm('Delete this reaction?')) return;
    await api.delete(`/reactions/${reactionId}`).catch(() => {});
    loadReactions();
  };

  // Group reactions by trigger event for display
  const grouped = {};
  reactions.forEach(r => {
    const key = `${r.assistant}_${r.trigger_event}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  return (
    <div className="space-y-4" data-testid="assistant-reactions-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917]">Assistant Reactions</h3>
          <p className="text-[10px] text-[#A8A29E]">Upload short animations for Taàbiy and Yeèbiy triggered by in-app events</p>
        </div>
        <Button size="sm" onClick={() => setShowUpload(!showUpload)}
          className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs" data-testid="add-reaction-btn">
          + Upload Reaction
        </Button>
      </div>

      {/* Upload form */}
      {showUpload && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid="reaction-upload-form">
          <div className="flex items-center gap-6 mb-4">
            {/* Assistant picker */}
            <div className="flex gap-3">
              {[{id: 'taabiy', name: 'Taàbiy', img: TAABIY_IMG}, {id: 'yeebiy', name: 'Yeèbiy', img: YEEBIY_IMG}].map(a => (
                <button key={a.id} onClick={() => setUploadForm(p => ({...p, assistant: a.id}))}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                    uploadForm.assistant === a.id ? 'bg-[#E8F5E9] ring-2 ring-[#4CAF50]' : 'hover:bg-[#F5F5F4]'
                  }`}
                  data-testid={`pick-${a.id}`}>
                  <img src={a.img} alt={a.name} className="w-12 h-12 object-contain" />
                  <span className="text-[10px] font-semibold text-[#1C1917]">{a.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Trigger Event *</label>
              <select value={uploadForm.trigger_event}
                onChange={(e) => setUploadForm(p => ({...p, trigger_event: e.target.value}))}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                data-testid="trigger-select">
                {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Label (optional)</label>
              <input value={uploadForm.label} onChange={(e) => setUploadForm(p => ({...p, label: e.target.value}))}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]"
                placeholder="e.g. Taàbiy happy dance" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Target Gender</label>
              <select value={uploadForm.target_gender} onChange={(e) => setUploadForm(p => ({...p, target_gender: e.target.value}))}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white">
                <option value="all">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#57534E]">Target Age Group</label>
              <select value={uploadForm.target_age_group} onChange={(e) => setUploadForm(p => ({...p, target_age_group: e.target.value}))}
                className="w-full mt-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white">
                <option value="all">All</option>
                <option value="child">Child (under 12)</option>
                <option value="teen">Teen (12-17)</option>
                <option value="adult">Adult (18+)</option>
                <option value="senior">Senior</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-colors ${
              uploading ? 'bg-[#E7E5E4] text-[#A8A29E]' : 'bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9]'
            }`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Choose Animation File'}
              <input type="file" accept="image/gif,image/webp,image/png,video/webm,video/mp4" className="hidden"
                disabled={uploading} onChange={handleUpload} data-testid="reaction-file-input" />
            </label>
            <span className="text-[10px] text-[#A8A29E]">GIF, WebP, APNG, WebM, MP4 (max 5MB)</span>
            <Button size="sm" variant="outline" onClick={() => setShowUpload(false)} className="rounded-xl text-xs ml-auto">Cancel</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={filterAssistant} onChange={(e) => setFilterAssistant(e.target.value)}
          className="rounded-xl border-0 py-1.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
          data-testid="filter-assistant">
          <option value="">All Assistants</option>
          <option value="taabiy">Taàbiy</option>
          <option value="yeebiy">Yeèbiy</option>
        </select>
        <select value={filterTrigger} onChange={(e) => setFilterTrigger(e.target.value)}
          className="rounded-xl border-0 py-1.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
          data-testid="filter-trigger">
          <option value="">All Triggers</option>
          {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Reactions grid */}
      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : reactions.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="reactions-grid">
          {reactions.map(r => {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const previewUrl = `${backendUrl}/api/reactions/play/${r.reaction_id}`;
            return (
              <div key={r.reaction_id} className={`bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden ${!r.is_active ? 'opacity-40' : ''}`}
                data-testid={`reaction-${r.reaction_id}`}>
                <div className="aspect-square bg-[#F5F5F4] flex items-center justify-center p-2">
                  {r.content_type?.startsWith('video') ? (
                    <video src={previewUrl} autoPlay loop muted playsInline className="w-full h-full object-contain rounded-lg" />
                  ) : (
                    <img src={previewUrl} alt={r.label} className="w-full h-full object-contain" />
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <img src={r.assistant === 'taabiy' ? TAABIY_IMG : YEEBIY_IMG} alt="" className="w-4 h-4 object-contain" />
                    <span className="text-[10px] font-semibold text-[#1C1917] capitalize">{r.assistant}</span>
                  </div>
                  <p className="text-[10px] text-[#57534E] truncate">{r.label}</p>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                    r.is_active ? 'bg-[#EAF1EA] text-[#588157]' : 'bg-[#F5F5F4] text-[#A8A29E]'
                  }`}>{TRIGGER_LABELS[r.trigger_event] || r.trigger_event}</span>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => handleToggle(r)}
                      className="text-[9px] font-semibold px-2 py-0.5 rounded-lg bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]">
                      {r.is_active ? 'Pause' : 'Activate'}
                    </button>
                    <button onClick={() => handleDelete(r.reaction_id)}
                      className="text-[9px] font-semibold px-2 py-0.5 rounded-lg bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9]">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="flex justify-center gap-4 mb-3">
            <img src={TAABIY_IMG} alt="Taàbiy" className="w-16 h-16 object-contain" />
            <img src={YEEBIY_IMG} alt="Yeèbiy" className="w-16 h-16 object-contain" />
          </div>
          <p className="text-sm text-[#57534E]">No reactions uploaded yet</p>
          <p className="text-[10px] text-[#A8A29E] mt-1">Upload short animations (GIF, WebP, WebM) for different in-app events</p>
        </div>
      )}
    </div>
  );
}

function BotManager() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => { loadBots(); }, []);

  const loadBots = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bots');
      setBots(res.data.bots || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const seedBots = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/bots/seed');
      alert(`Created: ${res.data.created}, Existing: ${res.data.existing}`);
      loadBots();
    } catch (err) { console.error(err) }
    setSeeding(false);
  };

  const toggleAll = async (active) => {
    await api.post('/bots/toggle-all', { is_active: active }).catch(() => {});
    loadBots();
  };

  const runCycle = async () => {
    setRunning(true);
    try {
      const res = await api.post('/bots/run-cycle');
      setLastRun(res.data);
      loadBots();
    } catch (err) { console.error(err) }
    setRunning(false);
  };

  const updateBot = async (configId, updates) => {
    await api.put(`/bots/${configId}`, updates).catch(() => {});
    loadBots();
  };

  const activeCount = bots.filter(b => b.is_active).length;
  const totalPosts = bots.reduce((s, b) => s + (b.post_count || 0), 0);
  const totalLikes = bots.reduce((s, b) => s + (b.like_count || 0), 0);

  return (
    <div className="space-y-4" data-testid="bot-manager">
      {/* Header + actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917]">Community Bots</h3>
          <p className="text-[10px] text-[#A8A29E]">Automated accounts that post wisdom/proverbs and engage with community content</p>
        </div>
        <div className="flex gap-2">
          {bots.length === 0 && (
            <Button size="sm" onClick={seedBots} disabled={seeding}
              className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs" data-testid="seed-bots-btn">
              {seeding ? 'Creating...' : 'Create 25 Bots'}
            </Button>
          )}
          {bots.length > 0 && (
            <>
              <Button size="sm" variant="outline" onClick={() => toggleAll(true)} className="rounded-xl text-xs" data-testid="activate-all-btn">
                Activate All
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggleAll(false)} className="rounded-xl text-xs" data-testid="deactivate-all-btn">
                Deactivate All
              </Button>
              <Button size="sm" onClick={runCycle} disabled={running}
                className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl text-xs" data-testid="run-cycle-btn">
                {running ? 'Running...' : 'Run Activity Cycle'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {bots.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          <div className="bg-white rounded-xl border border-[#E7E5E4] px-3 py-2 text-center">
            <p className="text-lg font-bold text-[#1C1917]">{bots.length}</p>
            <p className="text-[9px] text-[#A8A29E]">Total Bots</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E7E5E4] px-3 py-2 text-center">
            <p className={`text-lg font-bold ${activeCount > 0 ? 'text-[#588157]' : 'text-[#A8A29E]'}`}>{activeCount}</p>
            <p className="text-[9px] text-[#A8A29E]">Active</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E7E5E4] px-3 py-2 text-center">
            <p className="text-lg font-bold text-[#4CAF50]">{totalPosts}</p>
            <p className="text-[9px] text-[#A8A29E]">Posts</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E7E5E4] px-3 py-2 text-center">
            <p className="text-lg font-bold text-[#D4A373]">{totalLikes}</p>
            <p className="text-[9px] text-[#A8A29E]">Likes</p>
          </div>
        </div>
      )}

      {/* Last run result */}
      {lastRun && (
        <div className="bg-[#EAF1EA] rounded-xl border border-[#588157]/20 p-3 text-xs text-[#588157]" data-testid="last-run-result">
          Cycle complete: {lastRun.posts_created} posts created, {lastRun.likes_given} likes given ({lastRun.active_bots} active bots)
        </div>
      )}

      {/* Bot list */}
      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : bots.length > 0 ? (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid="bot-list">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E7E5E4] bg-[#F5F5F4]">
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Active</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Like</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Post</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Posts/Day</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Stats</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bots.map(bot => (
                  <tr key={bot.config_id} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]" data-testid={`bot-${bot.config_id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${bot.is_active ? 'bg-[#E8F5E9] text-[#4CAF50]' : 'bg-[#F5F5F4] text-[#A8A29E]'}`}>
                          {bot.bot_name?.charAt(0)}
                        </div>
                        <span className="font-medium text-[#1C1917]">{bot.bot_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => updateBot(bot.config_id, {...bot, is_active: !bot.is_active})}
                        className={`w-8 h-4 rounded-full relative transition-colors ${bot.is_active ? 'bg-[#588157]' : 'bg-[#E7E5E4]'}`}>
                        <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${bot.is_active ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => updateBot(bot.config_id, {...bot, auto_like: !bot.auto_like})}
                        className={`w-8 h-4 rounded-full relative transition-colors ${bot.auto_like ? 'bg-[#588157]' : 'bg-[#E7E5E4]'}`}>
                        <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${bot.auto_like ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => updateBot(bot.config_id, {...bot, auto_post: !bot.auto_post})}
                        className={`w-8 h-4 rounded-full relative transition-colors ${bot.auto_post ? 'bg-[#588157]' : 'bg-[#E7E5E4]'}`}>
                        <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${bot.auto_post ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <select value={bot.posts_per_day}
                        onChange={(e) => updateBot(bot.config_id, {...bot, posts_per_day: parseInt(e.target.value)})}
                        className="rounded-lg border-0 py-0.5 px-1 text-[10px] ring-1 ring-inset ring-[#E7E5E4] bg-white">
                        {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-[#A8A29E]">
                      {bot.post_count || 0}p / {bot.like_count || 0}l
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => updateBot(bot.config_id, {...bot, is_active: !bot.is_active})}
                        className="text-[9px] font-semibold px-2 py-0.5 rounded-lg bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]">
                        {bot.is_active ? 'Pause' : 'Start'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-sm text-[#57534E]">No bots created yet</p>
          <p className="text-[10px] text-[#A8A29E] mt-1">Click "Create 25 Bots" to set up community engagement bots</p>
        </div>
      )}
    </div>
  );
}


function ReviewedWordsList() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => { loadReviews(); }, [page]); // eslint-disable-line

  const loadReviews = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/reviewed-translations?limit=30&skip=${page * 30}`);
      setReviews(res.data.reviews || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'});
  };

  return (
    <div data-testid="reviewed-words-list">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#1C1917]">Reviewed Translations</h3>
          <p className="text-[10px] text-[#A8A29E]">{total} total reviews by Elders</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : reviews.length > 0 ? (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E7E5E4] bg-[#F5F5F4]">
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Lamnso'</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">English</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Action</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Reviewer</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Date</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-[#57534E]">Audio</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r, i) => (
                  <tr key={r.review_id || `rev-${i}`} className="border-b border-[#E7E5E4] hover:bg-[#FAFAF9]">
                    <td className="px-3 py-2.5 font-medium text-[#1C1917]">{r.translation?.lamnso_text || '—'}</td>
                    <td className="px-3 py-2.5 text-[#57534E]">{r.translation?.english_text || '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        r.action === 'confirm' ? 'bg-[#EAF1EA] text-[#588157]' :
                        r.action === 'reject' ? 'bg-red-50 text-red-600' :
                        'bg-[#FDF6F0] text-[#D4A373]'
                      }`}>{r.action}</span>
                    </td>
                    <td className="px-3 py-2.5 text-[#A8A29E]">{r.reviewer_name}</td>
                    <td className="px-3 py-2.5 text-[#A8A29E]">{formatTime(r.timestamp)}</td>
                    <td className="px-3 py-2.5">
                      {r.has_pronunciation ? (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#EAF1EA] text-[#588157]">Has audio</span>
                      ) : (
                        <a href={`/validate?focus=${r.translation_id}`} target="_blank" rel="noopener noreferrer"
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]">
                          + Add
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 30 && (
            <div className="flex items-center justify-center gap-3 p-3 border-t border-[#E7E5E4]">
              <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
                className="text-xs font-semibold text-[#57534E] disabled:opacity-30">Previous</button>
              <span className="text-[10px] text-[#A8A29E]">Page {page+1} of {Math.ceil(total/30)}</span>
              <button onClick={() => setPage(p => p+1)} disabled={(page+1)*30 >= total}
                className="text-xs font-semibold text-[#57534E] disabled:opacity-30">Next</button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-center text-xs text-[#A8A29E] py-8">No reviewed translations yet</p>
      )}
    </div>
  );
}

